/**
 * TrendForecastingPage — `/reporting/forecasting`.
 *
 * Holt-Winters forecast UI. Wired against
 * `POST /v1/reporting-ml/forecasts`.
 */
import { type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LineChart } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { runForecast, type ForecastRunWire } from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function TrendForecastingPage(): JSX.Element {
  const [metric, setMetric] = useState('mail.volume.daily');
  const [horizon, setHorizon] = useState(7);
  const [seasonLength, setSeasonLength] = useState(7);
  const [history, setHistory] = useState('120, 132, 145, 138, 150, 162, 158');

  const forecast = useMutation<ForecastRunWire, Error>({
    mutationFn: () => {
      const series = history
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n));
      return runForecast({
        metric,
        history: series,
        horizon,
        seasonLength,
      });
    },
  });

  const denied =
    forecast.isError && forecast.error instanceof ApiError &&
    (forecast.error.status === 401 || forecast.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trend forecasting"
        description="Holt-Winters forecasts."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          forecast.mutate();
        }}
        className="bg-white border border-border rounded-xl p-5 space-y-3"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            placeholder="metric"
            aria-label="Metric"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="number"
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value) || 1)}
            placeholder="horizon"
            aria-label="Horizon"
            min={1}
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="number"
            value={seasonLength}
            onChange={(e) => setSeasonLength(Number(e.target.value) || 1)}
            placeholder="season length"
            aria-label="Season length"
            min={1}
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <textarea
          value={history}
          onChange={(e) => setHistory(e.target.value)}
          placeholder="comma-separated historical values"
          aria-label="History"
          rows={3}
          className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg"
        />
        <button
          type="submit"
          disabled={forecast.isPending}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
        >
          <LineChart className="w-4 h-4" />
          {forecast.isPending ? 'Running…' : 'Run forecast'}
        </button>
      </form>

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/runForecast</code> required.
        </DeferredNotice>
      ) : forecast.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {forecast.error.message}
        </DeferredNotice>
      ) : forecast.data ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">
            Forecast: {forecast.data.metric}
          </h3>
          <p className="text-xs text-muted mb-3">
            method=<code className="font-mono">{forecast.data.method}</code>{' '}
            confidence=<code className="font-mono">{forecast.data.confidenceLevel}</code>{' '}
            inSampleRms=<code className="font-mono">{forecast.data.inSampleRms.toFixed(3)}</code>
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Step</th>
                  <th className="text-right px-3 py-2">Value</th>
                  <th className="text-right px-3 py-2">Lower</th>
                  <th className="text-right px-3 py-2">Upper</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {forecast.data.forecast.map((p) => (
                  <tr key={p.stepIndex}>
                    <td className="px-3 py-2 text-xs">{p.stepIndex}</td>
                    <td className="px-3 py-2 text-right text-xs font-medium">{p.value.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-xs">{p.lower.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-xs">{p.upper.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ForecastSparkline points={forecast.data.forecast} />
        </div>
      ) : null}
    </div>
  );
}

function ForecastSparkline({
  points,
}: {
  points: readonly { stepIndex: number; value: number; lower: number; upper: number }[];
}): JSX.Element {
  if (points.length === 0) return <p className="text-xs text-muted mt-3">No forecast points.</p>;
  const W = 600;
  const H = 120;
  const PAD = 24;
  const all = points.flatMap((p) => [p.value, p.lower, p.upper]);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const xStep = (W - PAD * 2) / Math.max(1, points.length - 1);
  const y = (v: number): number => H - PAD - ((v - min) / range) * (H - PAD * 2);
  const x = (i: number): number => PAD + i * xStep;
  const valuePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.value)}`).join(' ');
  const bandPath =
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.upper)}`).join(' ') +
    ' ' +
    points
      .slice()
      .reverse()
      .map((p, i) => `L${x(points.length - 1 - i)},${y(p.lower)}`)
      .join(' ') +
    ' Z';
  return (
    <div className="mt-4" data-testid="forecast-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Forecast chart">
        <path d={bandPath} fill="rgba(34,197,94,0.15)" stroke="none" />
        <path d={valuePath} stroke="#22c55e" strokeWidth={2} fill="none" />
        {points.map((p, i) => (
          <circle key={p.stepIndex} cx={x(i)} cy={y(p.value)} r={2.5} fill="#22c55e" />
        ))}
      </svg>
      <p className="text-[10px] text-muted mt-1">
        Green line: forecast value · band: confidence interval [{min.toFixed(1)}, {max.toFixed(1)}]
      </p>
    </div>
  );
}
